import { Box, Typography } from '@mui/material';
import CustomContainer from '../../components/CustomContainer';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import EmptyState from '../../components/EmptyState';
import AppCard from '../../components/ui/AppCard';
import FormSection from '../../components/ui/FormSection';
import Eyebrow from '../../components/ui/Eyebrow';
import Price from '../../components/ui/Price';
import { SpecItem, SpecList } from '../../components/ui/SpecList';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LoadingComponent from '../../components/LoadingComponent';
import { useMemo } from 'react';
import {
    getCategories, getComforts, getExternalFinishings, getFormats, getPlatings,
} from '../../helpers/httpRequests/product_props';
import { getOrderDetails } from '../../helpers/httpRequests/orders';
import { formatOptions, moneyFormat } from '../../utils/functions';
import { metalList, orderStatusInfo, orderStatusStages } from '../../utils/variables';
import { useModal } from '../../contexts/ModalProvider';
import dayjs from 'dayjs';

function isEmptyValue(val) {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    if (val === 0 || val === 0.0 || val === '0' || val === '0.00') return true;
    return false;
}

function optionLabel(options, value) {
    if (isEmptyValue(value)) return null;
    const found = options?.find((opt) => String(opt.value) === String(value));
    return found?.label || String(value);
}

function formatDate(value) {
    if (isEmptyValue(value)) return null;
    const parsed = dayjs(value);
    if (parsed.isValid() && /\d{4}/.test(String(value))) {
        return parsed.format('DD/MM/YYYY');
    }
    return String(value);
}

function formatPrice(value) {
    if (value == null || value === '' || value === 0 || value === '0') return null;
    const text = String(value).trim();
    if (text.includes('R$')) return text;
    return moneyFormat(value);
}

function withUnit(value, unit) {
    if (isEmptyValue(value)) return null;
    return `${value} ${unit}`;
}

export default function OrderDetails() {
    const { setImage, handleOpen } = useModal();
    const { id } = useParams();

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 60 });
    const formatsQuery = useQuery({ queryKey: ['getFormats'], queryFn: getFormats, staleTime: 1000 * 60 });
    const comfortsQuery = useQuery({ queryKey: ['getComforts'], queryFn: getComforts, staleTime: 1000 * 60 });
    const platingsQuery = useQuery({ queryKey: ['getPlatings'], queryFn: getPlatings, staleTime: 1000 * 60 });
    const externalFinishingsQuery = useQuery({
        queryKey: ['getExternalFinishings'],
        queryFn: getExternalFinishings,
        staleTime: 1000 * 60,
    });
    const orderQuery = useQuery({
        queryKey: ['getOrderDetails', id],
        queryFn: () => getOrderDetails(id),
        staleTime: 1000 * 60,
    });

    const isFetching = [
        categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, externalFinishingsQuery, orderQuery,
    ].some((q) => q.isFetching);

    const categories = formatOptions(categoriesQuery.data);
    const formats = formatOptions(formatsQuery.data);
    const comforts = formatOptions(comfortsQuery.data);
    const platings = formatOptions(platingsQuery.data);
    const externalFinishings = formatOptions(externalFinishingsQuery.data);

    const order = useMemo(() => orderQuery.data?.data?.data ?? {}, [orderQuery.data]);
    const images = useMemo(
        () => (Array.isArray(order.images) ? order.images.filter((image) => !isEmptyValue(image)) : []),
        [order.images],
    );
    const stones = Array.isArray(order.stones) ? order.stones : [];
    const extras = Array.isArray(order.extras) ? order.extras : [];
    const priceLabel = formatPrice(order.price);

    const hasJewelry = [
        order.name, order.category, order.format, order.confort, order.height,
        order.width, order.thickness, order.plating, order.finishing,
    ].some((value) => !isEmptyValue(value))
        || (order.is_crafted !== null && order.is_crafted !== undefined);

    const hasMetal = [
        order.selected_metal, order.high_gold_weight, order.low_gold_weight, order.silver_weight,
    ].some((value) => !isEmptyValue(value));

    const hasDetails = [
        order.observation, order.public_description,
        order.size_one, order.size_two, order.ring_size_one, order.ring_size_two,
        order.engraving_one, order.engraving_two,
    ].some((value) => !isEmptyValue(value));

    const openImage = (image) => {
        setImage(image);
        handleOpen();
    };

    if (isFetching) return <LoadingComponent />;

    if (!order.id) {
        return (
            <CustomContainer>
                <Box sx={{ my: 4 }}>
                    <PageHeader title="Pedido" crumbs={[{ label: 'Meus pedidos', to: '/pedidos' }, 'Detalhes']} />
                    <AppCard>
                        <EmptyState
                            title="Pedido não encontrado"
                            description="Esse pedido não está disponível ou o link está incompleto."
                            actionLabel="Ver meus pedidos"
                            actionTo="/pedidos"
                        />
                    </AppCard>
                </Box>
            </CustomContainer>
        );
    }

    return (
        <CustomContainer>
            <Box sx={{ my: 4 }}>
                <PageHeader
                    title={order.name || `Pedido #${order.id}`}
                    crumbs={[{ label: 'Meus pedidos', to: '/pedidos' }, `Pedido #${order.id}`]}
                    action={
                        <StatusChip
                            color={orderStatusInfo[order.status]?.color}
                            bg={orderStatusInfo[order.status]?.bg}
                            label={orderStatusInfo[order.status]?.label}
                        />
                    }
                />

                <AppCard sx={{ mb: 4 }}>
                    <Eyebrow>Produção</Eyebrow>
                    <OrderTimeline status={order.status} />
                </AppCard>

                <AppCard sx={{ mb: 4 }}>
                    {images.length > 0 && (
                        <Box sx={styles.gallery}>
                            {images.map((image, index) => (
                                <Box
                                    key={`${image}-${index}`}
                                    component="button"
                                    type="button"
                                    onClick={() => openImage(image)}
                                    sx={styles.thumbBtn}
                                >
                                    <Box
                                        component="img"
                                        src={image}
                                        alt={`${order.name || 'Joia'}, foto ${index + 1}`}
                                        sx={styles.thumb}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Box sx={styles.summary}>
                        <SpecList columns={3}>
                            <SpecItem label="Pedido" value={`#${order.id}`} />
                            <SpecItem label="Metal" value={optionLabel(metalList, order.selected_metal)} />
                            <SpecItem label="Pedido em" value={formatDate(order.ordered_at)} />
                            <SpecItem label="Entrega" value={formatDate(order.delivery_date)} />
                        </SpecList>
                        {priceLabel && (
                            <Box sx={styles.priceBlock}>
                                <Typography sx={styles.priceLabel}>Valor do pedido</Typography>
                                <Price size="lg">{priceLabel}</Price>
                            </Box>
                        )}
                    </Box>
                </AppCard>

                {hasJewelry && (
                    <FormSection title="Especificações">
                        <SpecList>
                            <SpecItem label="Nome" value={order.name} />
                            <SpecItem label="Categoria" value={optionLabel(categories, order.category)} />
                            <SpecItem label="Formato" value={optionLabel(formats, order.format)} />
                            <SpecItem label="Conforto" value={optionLabel(comforts, order.confort)} />
                            <SpecItem label="Altura" value={withUnit(order.height, 'mm')} />
                            <SpecItem label="Largura" value={withUnit(order.width, 'mm')} />
                            <SpecItem label="Espessura" value={withUnit(order.thickness, 'mm')} />
                            <SpecItem label="Banho" value={optionLabel(platings, order.plating)} />
                            <SpecItem label="Acabamento externo" value={optionLabel(externalFinishings, order.finishing)} />
                            {order.is_crafted !== null && order.is_crafted !== undefined && (
                                <SpecItem label="Joia trabalhada / montada" value={order.is_crafted ? 'Sim' : 'Não'} />
                            )}
                        </SpecList>
                    </FormSection>
                )}

                {hasMetal && (
                    <FormSection title="Metal e peso">
                        <SpecList>
                            <SpecItem label="Material" value={optionLabel(metalList, order.selected_metal)} />
                            <SpecItem label="Ouro 18K" value={withUnit(order.high_gold_weight, 'g')} />
                            <SpecItem label="Ouro 10K" value={withUnit(order.low_gold_weight, 'g')} />
                            <SpecItem label="Prata" value={withUnit(order.silver_weight, 'g')} />
                        </SpecList>
                    </FormSection>
                )}

                {stones.length > 0 && (
                    <FormSection title="Pedras">
                        <Box>
                            {stones.map((stone, index) => (
                                <Box key={index} sx={styles.stoneRow}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={styles.stoneName}>{stone.stone_name}</Typography>
                                        {stone.variation_name && (
                                            <Typography sx={styles.stoneMeta}>{stone.variation_name}</Typography>
                                        )}
                                        {stone.observation && (
                                            <Typography sx={styles.stoneNote}>{stone.observation}</Typography>
                                        )}
                                    </Box>
                                    <Typography sx={styles.stoneQty}>
                                        {stone.quantity}
                                        <Box component="span" sx={styles.stoneQtyLabel}>un.</Box>
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </FormSection>
                )}

                {hasDetails && (
                    <FormSection title="Detalhes e gravação">
                        <SpecList>
                            <SpecItem label="Observação" value={order.observation} wide />
                            <SpecItem label="Descrição" value={order.public_description} wide />
                            <SpecItem label="Aro 1" value={order.size_one ?? order.ring_size_one} />
                            <SpecItem label="Aro 2" value={order.size_two ?? order.ring_size_two} />
                            <SpecItem label="Gravação 1" value={order.engraving_one} />
                            <SpecItem label="Gravação 2" value={order.engraving_two} />
                        </SpecList>
                    </FormSection>
                )}

                {extras.length > 0 && (
                    <FormSection title="Extras">
                        <Box sx={styles.extras}>
                            {extras.map((extra, index) => (
                                <Box key={extra.value || extra.label || index} sx={styles.extraChip}>
                                    {extra.label || extra}
                                </Box>
                            ))}
                        </Box>
                    </FormSection>
                )}
            </Box>
        </CustomContainer>
    );
}

function OrderTimeline({ status }) {
    const active = Number(status);

    return (
        <Box component="ol" sx={styles.track}>
            {orderStatusStages.map((label, index) => {
                const done = index <= active;
                const current = index === active;
                return (
                    <Box
                        component="li"
                        key={label}
                        aria-current={current ? 'step' : undefined}
                        sx={styles.step}
                    >
                        <Box sx={styles.stepRail} aria-hidden>
                            <Box sx={{ ...styles.rail, ...(index === 0 ? styles.railHidden : null), ...(done && index !== 0 ? styles.railOn : null) }} />
                            <Box sx={{ ...styles.dot, ...(done ? styles.dotOn : null), ...(current ? styles.dotCurrent : null) }} />
                            <Box sx={{ ...styles.rail, ...(index === orderStatusStages.length - 1 ? styles.railHidden : null), ...(index < active ? styles.railOn : null) }} />
                        </Box>
                        <Typography sx={{ ...styles.stepLabel, ...(current ? styles.stepLabelOn : null) }}>
                            {label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

const styles = {
    gallery: {
        display: 'flex',
        gap: 1.5,
        overflowX: 'auto',
        pb: 0.5,
        mb: 3,
    },
    thumbBtn: {
        flex: '0 0 auto',
        p: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'secondary.main',
            outlineOffset: 2,
        },
    },
    thumb: {
        display: 'block',
        width: 120,
        height: 120,
        objectFit: 'cover',
        aspectRatio: '1 / 1',
    },
    summary: {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
        gap: 3,
        alignItems: 'end',
    },
    priceBlock: {
        textAlign: { xs: 'left', md: 'right' },
    },
    priceLabel: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 0.75,
    },
    track: {
        listStyle: 'none',
        display: 'flex',
        m: 0,
        mt: 2.5,
        p: 0,
        overflowX: 'auto',
    },
    step: {
        flex: '1 0 5.5rem',
        minWidth: '5.5rem',
        textAlign: 'center',
    },
    stepRail: {
        display: 'flex',
        alignItems: 'center',
        mb: 1,
    },
    rail: {
        flex: 1,
        height: '1px',
        bgcolor: 'divider',
    },
    railOn: {
        bgcolor: 'secondary.main',
    },
    railHidden: {
        bgcolor: 'transparent',
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: 'divider',
        flexShrink: 0,
    },
    dotOn: {
        bgcolor: 'secondary.main',
    },
    dotCurrent: {
        outline: '4px solid',
        outlineColor: 'secondary.light',
    },
    stepLabel: {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'text.secondary',
        lineHeight: 1.3,
    },
    stepLabelOn: {
        fontWeight: 600,
        color: 'text.primary',
    },
    stoneRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': {
            pt: 0,
        },
        '&:last-of-type': {
            borderBottom: 'none',
            pb: 0,
        },
    },
    stoneName: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        textAlign: 'left',
    },
    stoneMeta: {
        mt: 0.25,
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'secondary.dark',
        textAlign: 'left',
    },
    stoneNote: {
        mt: 0.75,
        fontSize: '0.8125rem',
        color: 'text.secondary',
        fontStyle: 'italic',
        textAlign: 'left',
    },
    stoneQty: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        fontSize: '0.9375rem',
        flexShrink: 0,
    },
    stoneQtyLabel: {
        ml: 0.5,
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'text.secondary',
    },
    extras: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
    },
    extraChip: {
        px: 1.25,
        py: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
};
